import { useEffect, useState } from 'react';

/**
 * Detects if a media query matches the current viewport.
 * Useful for responsive breakpoint detection.
 * 
 * @param query - CSS media query string (e.g., '(min-width: 900px)')
 * @returns Boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Initialize with current match state
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    
    // Update state when media query match changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    // Clean up listener
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Common breakpoint hooks for convenience
 */
export const useIsMobile = () => useMediaQuery('(max-width: 899px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 900px)');
export const useIsTablet = () => useMediaQuery('(min-width: 600px) and (max-width: 899px)');
